<script>
import { supplier as supplierCy } from '@orderez/qa-utils/dist/orderEz/supplier';

import EzConfirmationModal from '@/components/ui/Modal/EzConfirmationModal.vue';
import EzButton from '@/components/ui/Button/EzButton.vue';

import ReturnInventoryToWarehouse from './ReturnInventoryToWarehouse.vue';

/**
 * ApproveModal
 * @version 1.0.0
 * @since 3.27.0
 */

export default {
  name: 'ApproveModal',
  props: {
    creditNote: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      required: false,
      default: 'approve'
    },
  },
  components: {
    EzConfirmationModal,
    EzButton,
    ReturnInventoryToWarehouse,
  },
  data() {
    return {
      warehouse: null,
      updateInventory: false,
      canUpdateInventory: false,
      supplierCy,
    };
  },
  computed: {
    hasProducts() {
      return !!this.creditNote.items.length;
    },
    allProductsUntracked() {
      return this.creditNote.items.every(i => !i.product.inventoryTracked);
    },
    warehouseDataCy() {
      const { LABEL__RETURN_TO_INVENTORY, BUTTON__WAREHOUSE } = supplierCy.ORDERS.CREDIT_NOTES.SINGLE_CREDIT_NOTE.APPROVE_MODAL;
      return { LABEL__RETURN_TO_INVENTORY, BUTTON__WAREHOUSE };
    },
    title(){
      const titleMapping = {
        approve: 'Approve Credit Note',
        cancel: 'Cancel Credit Note',
        complete: 'Mark as Credited',
      }
      return titleMapping[this.action]
    },
    returnInventoryConfig() {
      const configs = {
        approve: {
          show: true,
          showWarehouseDropdown: true,
          title: 'Return inventory to the warehouse',
        },
        complete: {
          show: false,
        },
        cancel: {
          show: this.canUpdateInventory,
          showWarehouseDropdown: false,
          title: 'Deduct inventory from the warehouse',
        },
      };
      return configs[this.action] ?? configs.approve;
    },
  },
  methods: {
    open() {
      const { warehouse, venue } = this.creditNote;
      const warehouseId = warehouse?.id || venue?.connection?.primaryWarehouseId;

      this.warehouse = this.allProductsUntracked ? null : warehouseId;
      this.canUpdateInventory = !this.allProductsUntracked
        && !!warehouse
        && this.creditNote.status === 'approved';
      this.$refs.modal.open();
    },
    close() {
      this.$refs.modal.close();
    },
    onCancel() {
      this.warehouse = null;
      this.close();
      this.$emit('cancel');
    },
    onApprove() {
      this.close();
      this.$emit('approve', this.action, this.warehouse, this.updateInventory);
    },
    onWarehouseChange(warehouse) {
      this.warehouse = warehouse;
    },
    onToggleChange(updateInventory) {
      this.updateInventory = updateInventory;
    },
  },
};
</script>

<template>
  <ez-confirmation-modal ref="modal" type="blue" icon="question">
    <template #title>{{title}}?</template>
    <template #content>
      Make sure you went through all credit note details.
      <hr />
      <return-inventory-to-warehouse
        v-if="hasProducts && returnInventoryConfig.show"
        :selected="warehouse"
        :toggleDisabled="allProductsUntracked"
        :showWarehouseDropdown="returnInventoryConfig.showWarehouseDropdown"
        :title="returnInventoryConfig.title"
        @change="onWarehouseChange"
        @toggleChange="onToggleChange"
        :data-cy="warehouseDataCy"
      />
    </template>
    <template #footer>
      <ez-button
        type="link"
        @click="onCancel"
        :data-cy="supplierCy.ORDERS.CREDIT_NOTES.SINGLE_CREDIT_NOTE.APPROVE_MODAL.BUTTON__CANCEL"
      >
        Cancel
      </ez-button>
      <ez-button
        type="primary"
        @click="onApprove"
        :data-cy="supplierCy.ORDERS.CREDIT_NOTES.SINGLE_CREDIT_NOTE.APPROVE_MODAL.BUTTON__APPROVE"
      >
        {{ title }}
      </ez-button>
    </template>
  </ez-confirmation-modal>
</template>

<style lang="scss" scoped></style>
